import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/services/firebase';
import useAuthStore from '@/stores/authStore';
import { firebaseApiClient } from '@/services/firebaseApi';
import { firebaseService } from '@/services/firebase-service';

export function FirebaseAuthProvider({ children }: { children: React.ReactNode }) {
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const currentState = useAuthStore.getState();

      if (firebaseUser && !currentState.isAuthenticated) {
        // User is signed in - load their profile data
        try {
          const userData = await firebaseApiClient.getCurrentUser();
          const authToken = await firebaseUser.getIdToken();

          useAuthStore.setState({
            user: userData.user,
            organization: userData.organization,
            token: authToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Fire-and-forget: record login event for analytics
          firebaseService.callFunction('recordLoginEvent', {}).catch(() => {});
        } catch (error) {
          console.error('Failed to load user profile:', error);
          useAuthStore.setState({
            user: null,
            organization: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      } else if (!firebaseUser && currentState.isAuthenticated) {
        // User is signed out - clear auth state
        useAuthStore.setState({
          user: null,
          organization: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }

      setInitializing(false);
    });

    return () => unsubscribe();
  }, []); // Empty deps — subscribe once, use getState() for current values

  // Show loading screen while initializing Firebase Auth
  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return <>{children}</>;
}